import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAllPartners, getActivityReferencesByPartner } from '../../../services/api';
import { createSubCoverage } from '../../../services/subCoverage.service';
import {
    extractFieldErrorsFromApi,
    formToSubCoveragePayload,
    getSubCoverageApiErrorMessage,
} from './subCoverage.mapper';

const getPartnersList = (response) => {
    if (response?.users && Array.isArray(response.users)) return response.users;
    if (Array.isArray(response)) return response;
    if (response?.partners && Array.isArray(response.partners)) return response.partners;
    if (response?.data && Array.isArray(response.data)) return response.data;
    return [];
};

/**
 * Hook métier : chargement partenaires/références + soumission création.
 */
export default function useSubCoverageCreate({ initialPartnerId = '', initialReferenceId = '' } = {}) {
    const { isAuthenticated } = useSelector((state) => state.adminAuth);

    const [partners, setPartners] = useState([]);
    const [references, setReferences] = useState([]);
    const [loadingPartners, setLoadingPartners] = useState(false);
    const [loadingReferences, setLoadingReferences] = useState(false);
    const [selectedPartnerId, setSelectedPartnerId] = useState(initialPartnerId || '');
    const [selectedReferenceId, setSelectedReferenceId] = useState(initialReferenceId || '');

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);
    const [createdData, setCreatedData] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) return undefined;
        let cancelled = false;

        const loadPartners = async () => {
            setLoadingPartners(true);
            try {
                const response = await getAllPartners();
                if (!cancelled) setPartners(getPartnersList(response));
            } catch (error) {
                console.error('Erreur chargement partenaires:', error);
                if (!cancelled) setPartners([]);
            } finally {
                if (!cancelled) setLoadingPartners(false);
            }
        };

        loadPartners();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    useEffect(() => {
        if (!selectedPartnerId) {
            setReferences([]);
            return undefined;
        }

        let cancelled = false;
        const loadReferences = async () => {
            setLoadingReferences(true);
            try {
                const response = await getActivityReferencesByPartner(selectedPartnerId);
                const refsList = response?.activity_references || [];
                if (cancelled) return;
                setReferences(refsList);
                setSelectedReferenceId((current) => {
                    if (!current) return current;
                    const stillExists = refsList.some(
                        (ref) => String(ref.reference_id || ref.id) === String(current)
                    );
                    return stillExists ? current : '';
                });
            } catch (error) {
                console.error('Erreur chargement références:', error);
                if (!cancelled) {
                    setReferences([]);
                    setSelectedReferenceId('');
                }
            } finally {
                if (!cancelled) setLoadingReferences(false);
            }
        };

        loadReferences();
        return () => {
            cancelled = true;
        };
    }, [selectedPartnerId]);

    const selectPartner = useCallback((partnerId) => {
        setSelectedPartnerId(partnerId || '');
        setSelectedReferenceId('');
        setSubmitError(null);
        setFieldErrors({});
    }, []);

    const selectReference = useCallback((referenceId) => {
        setSelectedReferenceId(referenceId || '');
        setSubmitError(null);
        setFieldErrors({});
    }, []);

    const clearSubmitFeedback = useCallback(() => {
        setSubmitError(null);
        setFieldErrors({});
        setSuccessMessage(null);
    }, []);

    const submit = useCallback(
        async (formValues) => {
            const referenceId = formValues.reference_id || selectedReferenceId;
            if (!referenceId) {
                setSubmitError('Veuillez sélectionner une référence.');
                return { ok: false };
            }

            setSubmitting(true);
            setSubmitError(null);
            setFieldErrors({});
            setSuccessMessage(null);

            try {
                const payload = formToSubCoveragePayload(formValues);
                const data = await createSubCoverage(referenceId, payload);
                setCreatedData(data);
                setSuccessMessage('La sous-couverture a été créée avec succès.');
                return { ok: true, data };
            } catch (error) {
                const apiFieldErrors = extractFieldErrorsFromApi(error);
                setFieldErrors(apiFieldErrors);
                setSubmitError(
                    getSubCoverageApiErrorMessage(
                        error,
                        "La sous-couverture n'a pas pu être créée. Vérifiez les informations saisies."
                    )
                );
                return { ok: false, error, fieldErrors: apiFieldErrors };
            } finally {
                setSubmitting(false);
            }
        },
        [selectedReferenceId]
    );

    return {
        partners,
        references,
        loadingPartners,
        loadingReferences,
        selectedPartnerId,
        selectedReferenceId,
        selectPartner,
        selectReference,
        submitting,
        submitError,
        fieldErrors,
        successMessage,
        createdData,
        clearSubmitFeedback,
        submit,
    };
}
